import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import apiService from '../services/api';
import Snackbar from './Snackbar';
import './TakeAssessment.css';
import GlobalHeader from './GlobalHeader';

function TakeAssessment() {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get employee data from navigation state
  const employeeData = location.state || {};
  
  // Set page title
  useEffect(() => {
    document.title = 'CR Capability Index - Take Assessment';
  }, []);

  const [associate, setAssociate] = useState('');
  const [assessmentType, setAssessmentType] = useState('');
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [scores, setScores] = useState({});
  const [comments, setComments] = useState({});
  const [urls, setUrls] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [cycleId, setCycleId] = useState(null);
  const [snackbar, setSnackbar] = useState({ show: false, message: '', type: 'success' });
  const [expandedQuestions, setExpandedQuestions] = useState({});
  const [expandedCategories, setExpandedCategories] = useState({});
  const [showPreview, setShowPreview] = useState(false);
  const [currentCycleName, setCurrentCycleName] = useState(null);
  const [noQuestionsAvailable, setNoQuestionsAvailable] = useState(false);
  
  // Assessment status and view mode states
  const [assessmentStatus, setAssessmentStatus] = useState(null);
  const [isViewMode, setIsViewMode] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [statusInitialized, setStatusInitialized] = useState(false);
  const [employeeRole, setEmployeeRole] = useState(null);
  const [employeeDepartment, setEmployeeDepartment] = useState(null);
  const [loadingEmployeeDetails, setLoadingEmployeeDetails] = useState(false);

  const rubricLabels = ['Beginner', 'Developing', 'Proficient', 'Advanced', 'Expert'];

  // Fetch current cycle name from /ci/cycle API
  useEffect(() => {
    const fetchCurrentCycle = async () => {
      try {
        const response = await apiService.fetchData('ci/cycle');
        if (response && response.status === 'success' && response.cycle) {
          const cycleName = response.cycle.Name;
          console.log('Current cycle name fetched:', cycleName);
          setCurrentCycleName(cycleName);
        }
      } catch (err) {
        console.error('Error fetching current cycle:', err);
      }
    };
    fetchCurrentCycle();
  }, []);

  // Fetch employee details (role and department) from tbl_Employees
  useEffect(() => {
    const fetchEmployeeDetails = async () => {
      try {
        setLoadingEmployeeDetails(true);
        
        // Determine which email to use
        const isManagerView = !!employeeData.employeeName && !employeeData.isManagerSelfAssessment;
        let emailToFetch = '';
        
        if (isManagerView) {
          // Manager is assessing an employee - use employee's email
          emailToFetch = employeeData.employeeEmail;
        } else {
          // Employee self-assessment or Manager self-assessment - use logged-in user's email
          emailToFetch = sessionStorage.getItem('user_emailId');
        }
        
        if (!emailToFetch) {
          console.log('No email available to fetch employee details');
          setLoadingEmployeeDetails(false);
          return;
        }
        
        console.log('Fetching employee details from tbl_Employees for:', emailToFetch);
        
        const response = await apiService.fetchData(`employees?user_email=${encodeURIComponent(emailToFetch)}`);
        
        if (response && response.status === 'success' && response.data) {
          const role = response.data.Role;
          const department = response.data.DepartmentName;
          
          console.log('Employee details fetched from DB:', { role, department });
          
          setEmployeeRole(role);
          setEmployeeDepartment(department);
        } else {
          console.log('Could not fetch employee details, falling back to session storage');
          // Fallback to session storage or navigation state
          if (isManagerView) {
            setEmployeeRole(employeeData.employeeRole);
            setEmployeeDepartment(employeeData.employeeDepartment);
          } else {
            setEmployeeRole(sessionStorage.getItem('job_title'));
            setEmployeeDepartment(sessionStorage.getItem('user_department'));
          }
        }
      } catch (err) {
        console.error('Error fetching employee details:', err);
        // Fallback to session storage or navigation state
        const isManagerView = !!employeeData.employeeName && !employeeData.isManagerSelfAssessment;
        if (isManagerView) {
          setEmployeeRole(employeeData.employeeRole);
          setEmployeeDepartment(employeeData.employeeDepartment);
        } else {
          setEmployeeRole(sessionStorage.getItem('job_title'));
          setEmployeeDepartment(sessionStorage.getItem('user_department'));
        }
      } finally {
        setLoadingEmployeeDetails(false);
      }
    };
    
    fetchEmployeeDetails();
  }, [employeeData.employeeEmail, employeeData.employeeName, employeeData.isManagerSelfAssessment]);

  // Set associate name and assessment type based on context
  // useEffect(() => {
  //   const userName = sessionStorage.getItem('user_name');
  //   const isEmployeeView = !!userName && !employeeData.isManagerSelfAssessment;
  //   const isManagerView = !!employeeData.employeeName && !employeeData.isManagerSelfAssessment;
  //   const isManagerSelfAssessment = !!employeeData.isManagerSelfAssessment;

  //   if (isManagerSelfAssessment) {
  //     // Manager taking their own assessment
  //     setAssociate(userName);
  //     setAssessmentType('self-assessment');
  //   } else if (isEmployeeView) {
  //     // Employee taking their own assessment
  //     setAssociate(userName);
  //     setAssessmentType('self-assessment');
  //   } else if (isManagerView) {
  //     // Manager taking assessment for an employee
  //     setAssociate(employeeData.employeeName);
  //     setAssessmentType('manager-assessment'); // Set assessment type for manager view
  //   }
  // }, []);

  // Set associate name and assessment type based on context
useEffect(() => {
  const loggedInUserName = sessionStorage.getItem('user_name') || 'User';

  // Determine context
  const isManagerAssessingEmployee = !!employeeData.employeeName && !employeeData.isManagerSelfAssessment;
  const isManagerDoingSelfAssessment = employeeData.isManagerSelfAssessment === true;
  const isEmployeeDoingSelfAssessment = !employeeData.employeeName && !employeeData.isManagerSelfAssessment;

  let displayName = loggedInUserName;
  let type = 'self-assessment';

  if (isManagerAssessingEmployee) {
    // Manager viewing or filling assessment FOR an employee
    displayName = employeeData.employeeName || loggedInUserName;
    type = 'manager-assessment';
  } else if (isManagerDoingSelfAssessment || isEmployeeDoingSelfAssessment) {
    // Anyone (employee or manager) doing or viewing their own assessment
    displayName = loggedInUserName;
    type = 'self-assessment';
  }

  setAssociate(displayName);
  setAssessmentType(type);
}, [employeeData.employeeName, employeeData.isManagerSelfAssessment]);

  // Fetch questions from API - wait for employee details to be loaded
  useEffect(() => {
    const fetchQuestions = async () => {
      // Wait for employee details to be loaded
      if (loadingEmployeeDetails) {
        console.log('Waiting for employee details to load...');
        return;
      }

      // If we don't have role yet and we're not loading, wait a bit
      if (!employeeRole && !loadingEmployeeDetails) {
        console.log('Employee role not yet available, waiting...');
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Check if this is a manager view (taking assessment for employee)
        const isManagerView = !!employeeData.employeeName && !employeeData.isManagerSelfAssessment;

        let questionsData = [];
        let cycleIdValue = null;

        if (isManagerView) {
          // Manager view - fetch questions from employee-reviews API
          const employeeEmail = employeeData.employeeEmail;
          cycleIdValue = employeeData.cycleId; // Use cycle ID from navigation data

          console.log('Fetching questions for manager assessment from employee-reviews API');
          console.log('Employee Email:', employeeEmail, 'Cycle ID:', cycleIdValue);

          if (cycleIdValue && employeeEmail) {
            // Fetch employee reviews to get the questions structure
            const reviewResponse = await apiService.fetchData(`employee-reviews?type=employee&email=${employeeEmail}&cycle_id=${cycleIdValue}`);

            console.log('Employee reviews API response:', reviewResponse);

            if (reviewResponse && reviewResponse.status === 'success' && reviewResponse.data && reviewResponse.data.length > 0) {
              const cycleData = reviewResponse.data.find(cycle => cycle.cycle_id === cycleIdValue);

              if (cycleData && cycleData.employees && cycleData.employees.length > 0) {
                const employeeDataFromAPI = cycleData.employees[0];

                if (employeeDataFromAPI.reviews && employeeDataFromAPI.reviews.length > 0) {
                  // Transform review data into question format
                  questionsData = employeeDataFromAPI.reviews.map(review => ({
                    ID: review.QuestionID,
                    Question: review.Question,
                    Description: review.Description || '',
                    CategoryID: review.CategoryID || 1, // Fallback if not available
                    CategoryName: review.CategoryName,
                    Weightage: review.Weightage || 10
                  }));

                  console.log('Questions extracted from employee reviews:', questionsData);
                } else {
                  console.log('No reviews found for employee:', employeeDataFromAPI);
                }
              } else {
                console.log('No employee data found in cycle:', cycleData);
              }
            } else {
              console.log('No review data found for employee:', employeeEmail, 'cycle:', cycleIdValue);
            }
          } else {
            console.log('Missing cycleId or employeeEmail:', { cycleIdValue, employeeEmail });
          }

          // Fallback: If no questions found from employee reviews, get questions based on employee's role from DB
          if (questionsData.length === 0) {
            console.log('No questions found from employee reviews, falling back to get-questions API');
            // Use role and department fetched from tbl_Employees
            const department = employeeDepartment || employeeData.employeeDepartment || '';
            const role = employeeRole || employeeData.employeeRole || '';

            const requestBody = {
              department: department,
              role: role
            };

            console.log('Fallback: Fetching questions with department and role from DB:', requestBody);

            const response = await apiService.postData('ci/getquestions', requestBody);
            console.log('Fallback API Response:', response);

            if (response && response.questions && response.questions.length > 0) {
              questionsData = response.questions;
              console.log('Questions from fallback API:', questionsData);
            }
          }
        } else {
          // Employee view or Manager self-assessment - fetch questions from get-questions API
          // Use role and department fetched from tbl_Employees (not session storage)
          const department = employeeDepartment || sessionStorage.getItem('user_department') || '';
          const role = employeeRole || sessionStorage.getItem('job_title') || '';

          // Prepare request body for the POST request
          const requestBody = {
            department: department,
            role: role
          };

          console.log('Fetching questions with department and role from DB:', requestBody);

          const response = await apiService.postData('ci/getquestions', requestBody);
          console.log('API Response:', response);

          if (response && response.questions) {
            questionsData = response.questions;
            cycleIdValue = response.cycleId || response.cycle_id || null;
          }
        }

        if (questionsData.length === 0) {
          // No questions found - display message in UI instead of throwing error
          setNoQuestionsAvailable(true);
          console.log('No questions found for the specified criteria');
        }

        console.log('Questions received:', questionsData);
        setQuestions(questionsData);
        setCycleId(cycleIdValue);

        // Initialize scores, comments and URLs for all questions
        const initialScores = {};
        const initialComments = {};
        const initialUrls = {};
        const initialExpanded = {};
        const initialCategoryExpanded = {};
        questionsData.forEach(q => {
          initialScores[q.ID] = null; // No default score
          initialComments[q.ID] = '';
          initialUrls[q.ID] = '';
          initialExpanded[q.ID] = false; // Start with questions collapsed
          // Initialize categories as expanded by default
          if (!initialCategoryExpanded[q.CategoryID]) {
            initialCategoryExpanded[q.CategoryID] = true;
          }
        });
        setScores(initialScores);
        setComments(initialComments);
        setUrls(initialUrls);
        setExpandedQuestions(initialExpanded);
        setExpandedCategories(initialCategoryExpanded);

        // Mark status as initialized since we have questions loaded
        setStatusInitialized(true);
      } catch (err) {
        console.error('Error fetching questions:', err);
        setError(err.message || 'Failed to fetch questions');
      } finally {
        setLoading(false);
      }
    };

    fetchQuestions();
  }, [employeeData.employeeName, employeeData.isManagerSelfAssessment, employeeRole, employeeDepartment, loadingEmployeeDetails]);

  // Fetch assessment status when cycleId is available
  useEffect(() => {
    const fetchAssessmentStatus = async () => {
      if (!cycleId) return;

      setStatusInitialized(true);
      try {
        setLoadingStatus(true);
        const userEmail = sessionStorage.getItem('user_emailId');
        const isManagerView = !!employeeData.employeeName && !employeeData.isManagerSelfAssessment;
        const isManagerSelfAssessment = !!employeeData.isManagerSelfAssessment;

        let apiUrl = '';

        if (isManagerView) {
          // Manager view - check if manager has submitted assessment for employees
          apiUrl = `assessment-status?cycle_id=${cycleId}&manager=${userEmail}`;
        } else {
          // Employee view or Manager self-assessment - check if user has submitted self-assessment
          apiUrl = `assessment-status?cycle_id=${cycleId}&employee=${userEmail}`;
        }

        console.log('Fetching assessment status:', apiUrl);
        const response = await apiService.fetchData(apiUrl);

        if (response) {
          setAssessmentStatus(response);

          if (isManagerView) {
            // Manager view logic - assessing employee
            const employeeEmail = employeeData.employeeEmail;

            // Check if manager has already submitted for this employee
            const managerSubmitted = response[employeeEmail] === true;

            if (managerSubmitted) {
              // Manager already submitted - switch to view mode
              setIsViewMode(true);
              setIsLocked(false);
            } else {
              // Check if employee email exists in response
              // If employee email is not present in the list, employee hasn't submitted
              const employeeEmailExists = employeeEmail in response;

              if (!employeeEmailExists) {
                // Employee hasn't submitted self-assessment - lock the manager assessment
                setIsLocked(true);
                setIsViewMode(false);
              } else {
                // Employee email exists in response (value is false, meaning manager hasn't reviewed yet)
                // Employee has submitted, manager can submit
                setIsLocked(false);
                setIsViewMode(false);
              }
            }
          } else {
            // Employee view logic or Manager self-assessment
            const userSubmitted = response.status === true;

            if (userSubmitted) {
              // User already submitted - switch to view mode
              setIsViewMode(true);
              setIsLocked(false);
            } else {
              // User can submit
              setIsViewMode(false);
              setIsLocked(false);
            }
          }
        }
      } catch (err) {
        console.error('Error fetching assessment status:', err);
        // Don't set error state here, let the component continue to load
      } finally {
        setLoadingStatus(false);
      }
    };

    fetchAssessmentStatus();
  }, [cycleId, employeeData.employeeEmail, employeeData.employeeName]);

  // Load existing assessment data when in view mode
  useEffect(() => {
    const loadExistingAssessment = async () => {
      if (!isViewMode || !cycleId) return;

      try {
        const userEmail = sessionStorage.getItem('user_emailId');
        const isManagerView = !!employeeData.employeeName && !employeeData.isManagerSelfAssessment;
        const isManagerSelfAssessment = !!employeeData.isManagerSelfAssessment;

        let apiUrl = '';
        if (isManagerView) {
          // Manager view - load manager's assessment for employee
          const employeeEmail = employeeData.employeeEmail;
          apiUrl = `employee-reviews?type=manager&email=${userEmail}&cycle_id=${cycleId}`;
        } else {
          // Employee view or Manager self-assessment - load user's self-assessment
          apiUrl = `employee-reviews?type=employee&email=${userEmail}&cycle_id=${cycleId}`;
        }

        console.log('Loading existing assessment:', apiUrl);
        const response = await apiService.fetchData(apiUrl);

        if (response && response.status === 'success' && response.data && response.data.length > 0) {
          const cycleData = response.data.find(cycle => cycle.cycle_id === cycleId);
          
          if (cycleData && cycleData.employees && cycleData.employees.length > 0) {
            let targetEmployee = null;
            
            if (isManagerView) {
              // Find the specific employee in manager view
              const employeeEmail = employeeData.employeeEmail;
              targetEmployee = cycleData.employees.find(emp => emp.email === employeeEmail);
            } else {
              // Get first employee in employee view
              targetEmployee = cycleData.employees[0];
            }

            if (targetEmployee && targetEmployee.reviews && targetEmployee.reviews.length > 0) {
              // Populate scores, comments and URLs from existing assessment
              const existingScores = {};
              const existingComments = {};
              const existingUrls = {};

              if (isManagerView) {
                // For manager view, use ManagerRating and ManagerComment
                targetEmployee.reviews.forEach(review => {
                  existingScores[review.QuestionID] = review.ManagerRating || 0;
                  existingComments[review.QuestionID] = review.ManagerComment || '';
                  existingUrls[review.QuestionID] = review.URLS || '';
                });
              } else {
                // For employee view, use SelfRating and SelfComment
                targetEmployee.reviews.forEach(review => {
                  existingScores[review.QuestionID] = review.SelfRating || 0;
                  existingComments[review.QuestionID] = review.SelfComment || '';
                  existingUrls[review.QuestionID] = review.URLS || '';
                });
              }

              setScores(existingScores);
              setComments(existingComments);
              setUrls(existingUrls);
              console.log('Loaded existing assessment data');
            }
          }
        }
      } catch (err) {
        console.error('Error loading existing assessment:', err);
      }
    };

    loadExistingAssessment();
  }, [isViewMode, cycleId, employeeData.employeeEmail, employeeData.employeeName]);

  // Group questions by category
  const groupedQuestions = questions.reduce((acc, question) => {
    const categoryId = question.CategoryID;
    if (!acc[categoryId]) {
      acc[categoryId] = {
        CategoryID: categoryId,
        CategoryName: question.CategoryName,
        questions: []
      };
    }
    acc[categoryId].questions.push(question);
    return acc;
  }, {});

  const categories = Object.values(groupedQuestions);
  console.log('Grouped questions:', groupedQuestions);
  console.log('Categories:', categories);

  const handleScoreChange = (questionId, score) => {
    if (isViewMode || isLocked) return; // Don't allow changes in view mode or when locked
    setScores(prev => ({
      ...prev,
      [questionId]: score
    }));
  };

  const handleCommentChange = (questionId, comment) => {
    if (isViewMode || isLocked) return; // Don't allow changes in view mode or when locked
    setComments(prev => ({
      ...prev,
      [questionId]: comment
    }));
  };

  const handleUrlChange = (questionId, url) => {
    if (isViewMode || isLocked) return; // Don't allow changes in view mode or when locked
    setUrls(prev => ({
      ...prev,
      [questionId]: url
    }));
  };

  const calculateTotalScore = () => {
    let total = 0;
    questions.forEach(question => {
      const score = scores[question.ID] || 0;
      const weightage = question.Weightage || 0;
      total += (score * weightage / 100);
    });
    return total.toFixed(2);
  };

  // Check if all ratings are filled
  const areAllRatingsFilled = () => {
    if (questions.length === 0) return false;
    return questions.every(question => {
      const score = scores[question.ID];
      return score !== null && score !== undefined && score >= 1 && score <= 5;
    });
  };

  const calculateWeightedScore = (score, weightage) => {
    return (score * weightage / 100).toFixed(2);
  };

  // Check if all questions are expanded
  const areAllQuestionsExpanded = () => {
    return questions.length > 0 && questions.every(q => expandedQuestions[q.ID]);
  };

  // Check if all questions are collapsed
  const areAllQuestionsCollapsed = () => {
    return questions.length > 0 && questions.every(q => !expandedQuestions[q.ID]);
  };

  // Check if all categories are expanded
  const areAllCategoriesExpanded = () => {
    return categories.length > 0 && categories.every(cat => expandedCategories[cat.CategoryID]);
  };

  // Check if all categories are collapsed
  const areAllCategoriesCollapsed = () => {
    return categories.length > 0 && categories.every(cat => !expandedCategories[cat.CategoryID]);
  };

  const toggleQuestionExpansion = (questionId) => {
    setExpandedQuestions(prev => ({
      ...prev,
      [questionId]: !prev[questionId]
    }));
  };

  const expandAllQuestions = () => {
    const allExpanded = {};
    questions.forEach(q => {
      allExpanded[q.ID] = true;
    });
    setExpandedQuestions(allExpanded);
  };

  const collapseAllQuestions = () => {
    const allCollapsed = {};
    questions.forEach(q => {
      allCollapsed[q.ID] = false;
    });
    setExpandedQuestions(allCollapsed);
  };

  const toggleCategoryExpansion = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const expandAllCategories = () => {
    const allExpanded = {};
    categories.forEach(cat => {
      allExpanded[cat.CategoryID] = true;
    });
    setExpandedCategories(allExpanded);
  };

  const collapseAllCategories = () => {
    const allCollapsed = {};
    categories.forEach(cat => {
      allCollapsed[cat.CategoryID] = false;
    });
    setExpandedCategories(allCollapsed);
  };

  const calculateCategoryTotal = (categoryQuestions) => {
    let total = 0;
    categoryQuestions.forEach(question => {
      const score = scores[question.ID] || 0;
      const weightage = question.Weightage || 0;
      total += (score * weightage / 100);
    });
    return total.toFixed(2);
  };

  const submitReview = async () => {
    // Get the logged-in user email from session storage
    const userEmail = sessionStorage.getItem('user_emailId');

    if (!userEmail) {
      setSubmitError('User email not found. Please log in again.');
      return;
    }

    // Log the user email being used
    console.log('Using user email from session storage:', userEmail);

    if (!cycleId) {
      setSubmitError('Unable to determine current assessment cycle. Please try refreshing the page.');
      return;
    }

    // Validate that all ratings are filled
    if (!areAllRatingsFilled()) {
      setSubmitError('Please fill all question ratings before submitting. Comments are optional.');
      setSnackbar({
        show: true,
        message: 'Please fill all question ratings before submitting. Comments are optional.',
        type: 'error'
      });
      return;
    }

    try {
      setSubmitting(true);
      setSubmitError(null);

      // Check assessment type
      const isManagerView = !!employeeData.employeeName && !employeeData.isManagerSelfAssessment;
      const isManagerSelfAssessment = !!employeeData.isManagerSelfAssessment;

      let apiUrl = '';
      let payload = {};

      if (isManagerView) {
        // Manager assessing employee - use manager review API
        const managerEmail = userEmail; // Manager's email (current user)
        const employeeEmail = employeeData.employeeEmail; // Employee's email

        if (!employeeEmail) {
          setSubmitError('Employee email not found. Please try again.');
          return;
        }

        apiUrl = `ci/review/manager/save/${managerEmail}/${employeeEmail}`;

        // Prepare manager responses array
        const responses = questions.map(question => ({
          QuestionID: question.ID,
          Question: question.Question,
          Description: question.Description || '',
          ManagerRating: scores[question.ID] !== null && scores[question.ID] !== undefined ? scores[question.ID] : 0,
          ManagerComment: comments[question.ID] || '',
          URLS: urls[question.ID] || ''
        }));

        // Prepare payload for manager review
        payload = {
          CycleID: cycleId,
          responses: responses
        };

        console.log('Manager assessment submission:', { managerEmail, employeeEmail, apiUrl, payload });

      } else {
        // Employee self-assessment or Manager self-assessment - use regular API
        apiUrl = `ci/review/save/${userEmail}`;

        // Prepare responses array for self-assessment
        const responses = questions.map(question => ({
          QuestionID: question.ID,
          Question: question.Question,
          Description: question.Description || '',
          SelfRating: scores[question.ID] !== null && scores[question.ID] !== undefined ? scores[question.ID] : 0,
          SelfComment: comments[question.ID] || '',
          URLS: urls[question.ID] || '',
          QuestionSource: 'Customized', // Default to Customized as shown in the example
          CategoryID: question.CategoryID,
          Weightage: question.Weightage
        }));

        // Prepare payload using role from database (tbl_Employees)
        payload = {
          CycleID: cycleId, // Use actual cycle ID from API only
          ManagerEmail: sessionStorage.getItem('manager_email') || '',
          DepartmentName: employeeDepartment || sessionStorage.getItem('user_department') || '',
          Role: employeeRole || sessionStorage.getItem('job_title') || '',
          responses: responses
        };
      }

      console.log('Submitting assessment:', { apiUrl, payload });

      // Call the appropriate API
      const response = await apiService.postData(apiUrl, payload);

      if (response.status === 'success') {
        const successMessage = isManagerView
          ? 'Manager assessment submitted successfully!'
          : isManagerSelfAssessment
          ? 'Self-assessment submitted successfully!'
          : 'Assessment submitted successfully!';
        setSnackbar({
          show: true,
          message: successMessage,
          type: 'success'
        });
        // Refresh assessment status after successful submission
        setIsViewMode(true);
        setIsLocked(false);
        // Navigate after a short delay to let user see the snackbar
        setTimeout(() => {
          window.scrollTo(0, 0);
          navigate(isManagerView ? '/Employees' : '/Home');
        }, 2000);
      } else {
        throw new Error(response.message || 'Failed to submit assessment');
      }

    } catch (err) {
      console.error('Error submitting review:', err);
      const errorMessage = err.message || 'Failed to submit assessment';
      setSubmitError(errorMessage);
      setSnackbar({
        show: true,
        message: 'Unable to submit the form. Form submission failed.',
        type: 'error'
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="take-assessment-container">
      <GlobalHeader />
      {/* Header */}
      <div className="ma-page-header">
        <button className="ma-back-button" onClick={() => {
          const isManagerView = !!employeeData.employeeName && !employeeData.isManagerSelfAssessment;
          window.scrollTo(0, 0);
          navigate(isManagerView ? '/Employees' : '/Home');
        }}>
          <span className="back-icon">←</span> Back to {employeeData.employeeName && !employeeData.isManagerSelfAssessment ? 'Employees' : 'Home'}
        </button>
        
        <div className="ma-header-content">
          <div className="ma-heading">
            <h1 className="page-title">Capability Index Assessment</h1>
            <p className="page-subtitle">
              {currentCycleName && (
                <span className="cycle-name-badge">Assessment Cycle: {currentCycleName}</span>
              )}
              {isViewMode && (
                <span className="view-mode-badge">📖 View Mode - Assessment Already Submitted</span>
              )}
              {isLocked && (
                <span className="locked-badge">🔒 Locked - Employee Has Not Submitted Self-Assessment</span>
              )}
            </p>
          </div>
        </div>
        
        <div className="assessment-controls">
          <div className="control-group">
            <label>Associate</label>
            <input
              type="text"
              value={associate}
              onChange={(e) => setAssociate(e.target.value)}
              placeholder="Employee name"
              disabled={!!sessionStorage.getItem('user_name') || !!employeeData.employeeName}
            />
          </div>

          <div className="total-score-box">
            <div className="total-score-label">Total CI Score</div>
            <div className="total-score-value">{calculateTotalScore()}</div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="assessment-content">
        {(loading || loadingStatus || loadingEmployeeDetails) && (
          <div className="loading-container">
            <p>{loadingEmployeeDetails ? 'Loading employee details...' : loading ? 'Loading questions...' : 'Loading assessment status...'}</p>
          </div>
        )}

        {error && (
          <div className="error-container">
            <p>Error: {error}</p>
          </div>
        )}

        {!loading && !loadingStatus && !loadingEmployeeDetails && !error && noQuestionsAvailable && (
          <div className="no-questions-container">
            <div className="no-questions-message">
              <h3>No Questions Available</h3>
              <p>No assessment questions are currently available for your role ({employeeRole || 'Unknown'}). Please contact your administrator.</p>
            </div>
          </div>
        )}

        {!loading && !loadingStatus && !loadingEmployeeDetails && !error && statusInitialized && !noQuestionsAvailable && (
          <>
            <div className="capability-ratings-header">
              <h2>Capability Ratings</h2>
              <p>Complete the assessment by rating questions on a 1-5 scale</p>
              <div className="expansion-controls">
                <button
                  className="expand-all-btn"
                  onClick={expandAllCategories}
                  disabled={areAllCategoriesExpanded()}
                >
                  Expand All Categories
                </button>
                <button
                  className="collapse-all-btn"
                  onClick={collapseAllCategories}
                  disabled={areAllCategoriesCollapsed()}
                >
                  Collapse All Categories
                </button>
                <button
                  className="expand-all-btn"
                  onClick={expandAllQuestions}
                  style={{ marginLeft: '8px' }}
                  disabled={areAllQuestionsExpanded() || areAllCategoriesCollapsed()}
                >
                  Expand All Questions
                </button>
                <button
                  className="collapse-all-btn"
                  onClick={collapseAllQuestions}
                  disabled={areAllQuestionsCollapsed() || areAllCategoriesCollapsed()}
                >
                  Collapse All Questions
                </button>
              </div>
            </div>

            <div className="capabilities-list">
              {categories.map((category) => (
                <div key={category.CategoryID} className="capability-block">
                  <div 
                    className="capability-header collapsible-category-header"
                    onClick={() => toggleCategoryExpansion(category.CategoryID)}
                  >
                    <div className="category-title-section">
                      <div className="category-expand-icon">
                        {expandedCategories[category.CategoryID] ? (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M18 15l-6-6-6 6"/>
                          </svg>
                        ) : (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M9 18l6-6-6-6"/>
                          </svg>
                        )}
                      </div>
                      <h3>{category.CategoryName} ({category.questions.reduce((total, q) => total + (q.Weightage || 0), 0)}%)</h3>
                    </div>
                    <div className="category-meta">
                      <span className="weight-label">
                        Category Score: {calculateCategoryTotal(category.questions)}
                      </span>
                    </div>
                  </div>
                  
                  {expandedCategories[category.CategoryID] && (
                  <div className="questions-list">
                    {category.questions.map((question) => (
                      <div key={question.ID} className="question-item">
                        <div
                          className="question-header collapsible-header"
                          onClick={() => toggleQuestionExpansion(question.ID)}
                        >
                          <div className="question-title-section">
                            <div className="expand-icon">
                              {expandedQuestions[question.ID] ? (
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M18 15l-6-6-6 6"/>
                                </svg>
                              ) : (
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M9 18l6-6-6-6"/>
                                </svg>
                              )}
                            </div>
                            <p className="question-text">
                              {question.Question} <span className="mandatory-asterisk" title="Mandatory">*</span>
                            </p>
                            {question.Description && (
                              <p className="question-description" style={{ fontSize: '13px', color: '#666', marginTop: '4px', fontStyle: 'italic' }}>
                                {question.Description}
                              </p>
                            )}
                          </div>
                          <div className="question-header-right">
                            <span className="question-weightage">Weightage: {question.Weightage}%</span>
                            {scores[question.ID] && (
                              <span className="current-score">Score: {scores[question.ID]}</span>
                            )}
                          </div>
                        </div>

                        {expandedQuestions[question.ID] && (
                          <div className="question-content">
                            <div className="score-section">
                              <div className="score-label">Score: {scores[question.ID] || 0}</div>

                              <div className="slider-container">
                                <div className="slider-track">
                                  {[1, 2, 3, 4, 5].map((value) => (
                                    <div key={value} className="slider-point">
                                      <div
                                        className={`slider-thumb ${scores[question.ID] === value ? 'active' : ''} ${isViewMode || isLocked ? 'view-mode' : ''}`}
                                        onClick={() => handleScoreChange(question.ID, value)}
                                        style={{ cursor: (isViewMode || isLocked) ? 'not-allowed' : 'pointer' }}
                                      >
                                        {value}
                                      </div>
                                      <div className="slider-label">{rubricLabels[value - 1]}</div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>

                            <div className="comments-section">
                              <label>Comments (Optional)</label>
                              <textarea
                                value={comments[question.ID] || ''}
                                onChange={(e) => handleCommentChange(question.ID, e.target.value)}
                                placeholder="Add any additional comments or context..."
                                rows="3"
                                readOnly={isViewMode || isLocked}
                                className={isViewMode || isLocked ? 'view-mode' : ''}
                              />
                            </div>

                            <div className="attachments-section">
                              <label>Attachment URLs (Optional)</label>
                              <input
                                type="text"
                                value={urls[question.ID] || ''}
                                onChange={(e) => handleUrlChange(question.ID, e.target.value)}
                                placeholder="Add attachment URLs (comma-separated for multiple)..."
                                readOnly={isViewMode || isLocked}
                                className={isViewMode || isLocked ? 'view-mode' : ''}
                              />
                            </div>

                            <div className="weighted-score">
                              Weighted Score: {calculateWeightedScore(scores[question.ID] || 0, question.Weightage || 0)}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  )}
                </div>
              ))}
            </div>

            {submitError && (
              <div className="submit-error">
                <p>Error: {submitError}</p>
              </div>
            )}

            <button
              className={`submit-assessment-btn ${isViewMode ? 'view-mode' : ''} ${isLocked ? 'locked' : ''} ${!areAllRatingsFilled() && !isViewMode && !isLocked ? 'disabled-incomplete' : ''}`}
              onClick={isViewMode ? () => {
                window.scrollTo(0, 0);
              } : () => setShowPreview(true)}
              disabled={submitting || isLocked || (!areAllRatingsFilled() && !isViewMode && !isLocked)}
              title={!areAllRatingsFilled() && !isViewMode && !isLocked ? 'Please fill all question ratings before submitting' : ''}
            >
              {isLocked ? '🔒 Not Submitted' :
               isViewMode ? '👁️ View Assessment' :
               !areAllRatingsFilled() && !isViewMode && !isLocked ? '🔒 Preview & Submit' :
               submitting ? 'Submitting...' : '👁️ Preview & Submit'}
            </button>
          </>
        )}
      </div>

      {/* Preview Modal */}
      {showPreview && (
        <div className="preview-modal-overlay">
          <div className="preview-modal">
            <div className="preview-modal-header">
              <h2>Assessment Preview</h2>
              <button
                className="preview-close-btn"
                onClick={() => setShowPreview(false)}
              >
                ✕
              </button>
            </div>

            <div className="preview-modal-content">
              <div className="preview-summary">
                <div className="preview-summary-item">
                  <span className="preview-label">Total CI Score:</span>
                  <span className="preview-value">{calculateTotalScore()}</span>
                </div>
                <div className="preview-summary-item">
                  <span className="preview-label">Questions Answered:</span>
                  <span className="preview-value">
                    {questions.filter(q => scores[q.ID] && scores[q.ID] >= 1 && scores[q.ID] <= 5).length} / {questions.length}
                  </span>
                </div>
              </div>

              <div className="preview-questions">
                {categories.map((category) => (
                  <div key={category.CategoryID} className="preview-category">
                    <h3 className="preview-category-title">{category.CategoryName}</h3>
                    <div className="preview-category-score">
                      Category Score: {calculateCategoryTotal(category.questions)}
                    </div>

                    {category.questions.map((question) => (
                      <div key={question.ID} className="preview-question-item">
                        <div className="preview-question-header">
                          <p className="preview-question-text">{question.Question}</p>
                          {question.Description && (
                            <p className="preview-question-description" style={{ fontSize: '13px', color: '#666', marginTop: '4px', fontStyle: 'italic' }}>
                              {question.Description}
                            </p>
                          )}
                          <div className="preview-question-meta">
                            <span className="preview-weightage">Weightage: {question.Weightage}%</span>
                            <span className="preview-score">Score: {scores[question.ID] || 'Not rated'}</span>
                          </div>
                        </div>

                        {comments[question.ID] && (
                          <div className="preview-comments">
                            <label>Comments:</label>
                            <p>{comments[question.ID]}</p>
                          </div>
                        )}

                        {urls[question.ID] && (
                          <div className="preview-urls">
                            <label>Attachment URLs:</label>
                            <p>{urls[question.ID]}</p>
                          </div>
                        )}

                        <div className="preview-weighted-score">
                          Weighted Score: {calculateWeightedScore(scores[question.ID] || 0, question.Weightage || 0)}
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>

            <div className="preview-modal-actions">
              <button
                className="preview-back-btn"
                onClick={() => setShowPreview(false)}
              >
                ← Back to Edit
              </button>
              <button
                className="preview-submit-btn"
                onClick={() => {
                  setShowPreview(false);
                  submitReview();
                }}
                disabled={submitting}
              >
                {submitting ? 'Submitting...' : '✅ Confirm & Submit'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Snackbar notification */}
      {snackbar.show && (
        <Snackbar
          message={snackbar.message}
          type={snackbar.type}
          duration={3000}
          onClose={() => setSnackbar({ show: false, message: '', type: 'success' })}
        />
      )}
    </div>
  );
}

export default TakeAssessment;
